import {
  isStoredEmailDiverged,
  setLocalStorageFromEmail,
} from 'utils/userInfoUtils';
import { storage, StorageKey } from 'utils/typedStorage';
import { withTimeout } from 'utils/withTimeout';
import { resolveRecruiterLanguage } from 'recruiter-i18n/languages';
import { getApiErrorStatus } from 'utils/apiErrorResponse';
import { create, type StateCreator } from 'zustand';
import { isAccountReady } from 'stores/sessionReadiness';
import { getAuth } from 'api/generated/api/auth';
import { usePermissionsStore } from 'stores/permissionsStore';
import { useSupportUserStore } from 'stores/supportUserStore';
import { resetSessionScopedStores } from 'stores/sessionResetRegistry';
import {
  clearVariableCatalogForTenantChange,
  prefetchVariableCatalog,
} from 'stores/variableCatalogCoordinator';
import type { AuthMember } from 'api/generated/types/authMember';
import type { AuthOrganization } from 'api/generated/types/authOrganization';
import type { AuthResponse } from 'api/generated/types/authResponse';
import type { UiLanguage } from 'api/generated/types/uiLanguage';
import { asIdentityId } from 'stores/sessionReadiness';
import {
  parseCachedSession,
  reconcileVerifiedSession,
  serializeCachedSession,
  sessionQualifiesForCache,
  usableCachedSession,
} from 'stores/sessionCache';
import {
  EMPTY_SESSION_IDENTITY,
  registerSessionIdentityReader,
  type SessionIdentity,
} from 'stores/sessionIdentity';

interface SessionState {
  isLoading: boolean;
  /**
   * The session on screen comes from the cache of the last verified session
   * and verification is still in flight. isLoading stays true meanwhile, so
   * only ProtectedRoute renders on it (see hydrateSessionFromCache).
   */
  isProvisional: boolean;
  isAuthenticated: boolean;
  supportAccess: boolean;
  authToken: string;
  member: AuthMember | null;
  organization: AuthOrganization | null;
}

const mirrorSessionIdentity = (
  orgId: string | null,
  userId: string | null
): void => {
  if (orgId) {
    storage.set(StorageKey.OrgId, orgId);
  } else {
    storage.remove(StorageKey.OrgId);
  }
  if (userId) {
    storage.set(StorageKey.UserId, userId);
  } else {
    storage.remove(StorageKey.UserId);
  }
};

export const identityFromSession = (
  isReady: boolean,
  member: AuthMember | null,
  organization: AuthOrganization | null
): SessionIdentity => {
  if (!isReady) {
    return EMPTY_SESSION_IDENTITY;
  }
  return {
    orgId: asIdentityId(organization?.salv_org_id),
    userId: asIdentityId(member?.salv_user_id),
    email: member?.email_address ?? null,
    orgName: organization?.organization_name ?? null,
    isReady: true,
  };
};

/** Applies the signed-in user's display language (see registerSessionLanguageApplier). */
export type SessionLanguageApplier = (
  uiLanguage: string | null | undefined
) => Promise<void>;

// Bounds how long verification waits on the language before marking the session
// ready. The applier bounds its own bundle load too; this also covers a hang.
export const SESSION_LANGUAGE_TIMEOUT_MS = 2500;

let sessionLanguageApplier: SessionLanguageApplier | null = null;

/**
 * Lets the recruiter and internal apps switch to the user's language before the
 * session is marked ready, so the first authenticated paint is already in it.
 * The internal app registers it from a lazy chunk, so a session verified before
 * that chunk loads gets its language from InternalToolsTabsPage instead.
 */
export const registerSessionLanguageApplier = (
  applier: SessionLanguageApplier | null
): void => {
  sessionLanguageApplier = applier;
};

const applySessionLanguage = async (
  uiLanguage: string | null | undefined
): Promise<void> => {
  const applier = sessionLanguageApplier;
  if (!applier) return;
  try {
    await withTimeout(applier(uiLanguage), SESSION_LANGUAGE_TIMEOUT_MS);
  } catch (error) {
    // swallow-ok: Keep the current language; language loading must not block sign-in.
    console.error('[Auth] applying the display language failed', error);
  }
};

// Widened from AuthMember: an older backend omits effective_ui_language even
// though the generated type marks it required.
type SessionMemberLanguages = Pick<AuthMember, 'ui_language'> & {
  effective_ui_language?: UiLanguage;
};

/** The member's resolved display language, else their own choice (null: none). */
export const sessionEffectiveLanguage = (
  member: SessionMemberLanguages
): UiLanguage | null =>
  member.effective_ui_language ?? member.ui_language ?? null;

interface SessionStore extends SessionState {
  logout: () => Promise<void>;
  invalidateLocalSession: () => void;
  setAuthenticatedSession: (authData: AuthResponse) => Promise<void>;
  verifySession: () => Promise<void>;
  setOrganizationUiLanguage: (uiLanguage: UiLanguage | null) => void;
}

// The signed-out session. Spread into the initial state and written whole on every
// unauthenticated outcome so the fields cannot drift between those sites.
const UNAUTHENTICATED_SESSION_STATE: SessionState = {
  isLoading: false,
  isProvisional: false,
  isAuthenticated: false,
  supportAccess: false,
  authToken: '',
  member: null,
  organization: null,
};

// A verify request started before the store's first verifySession (see
// prefetchSessionVerification), with the token it was sent for.
let prefetchedVerification: {
  token: string;
  promise: Promise<AuthResponse>;
} | null = null;

/**
 * Starts /auth/verify for the stored token before App.tsx evaluates, so the
 * round trip overlaps the app chunk download. verifySession consumes it once.
 * Runs after the support store is hydrated: the request's impersonation
 * headers are read from it.
 */
export const prefetchSessionVerification = (): void => {
  const token = storage.get(StorageKey.AuthToken);
  if (!token || prefetchedVerification) return;
  const promise = getAuth().authVerifyToken();
  // swallow-ok: verifySession handles this rejection; the prefetch may have no consumer yet.
  promise.catch(() => {});
  prefetchedVerification = { token, promise };
};

// The prefetched request when it was sent for `token`; either way the slot is
// emptied, so a later verify (refresh, retry) always sends a fresh request.
const takePrefetchedVerification = (
  token: string
): Promise<AuthResponse> | null => {
  const prefetched = prefetchedVerification;
  prefetchedVerification = null;
  return prefetched && prefetched.token === token ? prefetched.promise : null;
};

// Only the recruiter app renders from the cache (recruiterSessionBoot enables it);
// other builds that share this store neither read nor write it.
let sessionCacheEnabled = false;

export const enableSessionCache = (): void => {
  sessionCacheEnabled = true;
};

// Stores the verified session for the next load's optimistic render, or drops
// a cache the session no longer qualifies for. Writes browser storage only (an
// in-memory copy is useless to the next load) and returns false when the next
// load could still read a stale cache, so a reload must not rely on it.
const writeSessionCache = (
  authToken: string,
  member: AuthMember,
  organization: AuthOrganization
): boolean => {
  if (!sessionCacheEnabled) return true;
  const qualifies = sessionQualifiesForCache({
    member,
    organization,
    isSupportUser: useSupportUserStore.getState().isSupportUser,
  });
  if (
    qualifies &&
    storage.setPersisted(
      StorageKey.CachedSession,
      serializeCachedSession(authToken, member, organization)
    )
  ) {
    return true;
  }
  return storage.removePersisted(StorageKey.CachedSession);
};

// A logout, a 401 elsewhere or a new login replaced the stored token while verify
// was in flight; its result belongs to the old session and must not restore it.
const verifiedTokenChanged = (authToken: string): boolean =>
  storage.get(StorageKey.AuthToken) !== authToken;

// Components read permissions without subscribing, so the cached and the
// verified session both fill the store the same way.
const applyMemberPermissions = (member: AuthMember): void => {
  usePermissionsStore
    .getState()
    .setPermissions(
      member.role,
      member.permissions,
      member.permission_overrides
    );
};

const createSessionStore: StateCreator<SessionStore> = (set, get) => {
  let scopedResetFailed = false;
  const clearSessionSecurityState = (): void => {
    usePermissionsStore.getState().clearPermissions();
    useSupportUserStore.getState().reset();
    clearVariableCatalogForTenantChange();
  };
  const resetScopedStores = ({ clearStorageOnFailure = true } = {}): void => {
    try {
      resetSessionScopedStores();
      if (scopedResetFailed) clearSessionSecurityState();
      scopedResetFailed = false;
    } catch (error) {
      scopedResetFailed = true;
      // Hide protected UI before any subsequent login can expose retained data.
      if (clearStorageOnFailure) storage.clear();
      set(UNAUTHENTICATED_SESSION_STATE);
      try {
        clearSessionSecurityState();
      } catch (cleanupError) {
        throw new AggregateError(
          [error, cleanupError],
          'Session cleanup failed',
          { cause: cleanupError }
        );
      }
      throw error;
    }
  };
  // Applies verified auth data (from any auth endpoint) to the session store.
  // Shared by setAuthenticatedSession and verifySession. Login flows
  // now pass the generated-client AuthResponse, same as verify.
  const applyAuthData = async (authData: AuthResponse) => {
    const { auth_token, member, organization } = authData;
    const accountReady = isAccountReady(member, organization);
    const supportAccess = authData.support_access === true;

    // AuthResponse IDs are the runtime source of truth. Mirror them for
    // cross-tab invalidation only — getters read the session store.
    const orgId = asIdentityId(organization?.salv_org_id);
    const userId = asIdentityId(member?.salv_user_id);
    const previousOrgId = asIdentityId(get().organization?.salv_org_id);
    if (scopedResetFailed || (previousOrgId && previousOrgId !== orgId)) {
      // Signed into a different org without a logout in between: drop the
      // previous org's cached stores (feature flags, ATS config, calendar).
      resetScopedStores();
    }
    const support = useSupportUserStore.getState();
    if (supportAccess && !support.isSupportUser) {
      // A new support login starts at the selector, without a prior user's context.
      support.reset();
      support.setSupportUser();
    } else if (!supportAccess && support.isSupportUser) {
      support.reset();
    }
    storage.set(StorageKey.AuthToken, auth_token);
    mirrorSessionIdentity(orgId, userId);

    // Support profile selection is explicit. Looking up the actor's personal email
    // here would select that account for impersonation and overwrite a saved target.
    const storedEmail = storage.get(StorageKey.Email);
    const emailDiverged = isStoredEmailDiverged(
      storedEmail,
      member.email_address
    );
    if (accountReady && !supportAccess && emailDiverged) {
      // Stored identity diverged: setLocalStorageFromEmail hard-reloads to
      // re-authenticate. Skip the state update so the login route doesn't
      // client-navigate on top of the in-flight reload.
      const result = await setLocalStorageFromEmail(member.email_address);
      // Do not apply old permissions, language or cached identity after the
      // awaited profile lookup lost ownership to logout or another login.
      if (result === 'stale' || verifiedTokenChanged(auth_token)) return;
      if (result === 'reloading') {
        return;
      }
    }

    const shown = get();
    if (shown.isProvisional && shown.member && shown.organization) {
      const reconciliation = reconcileVerifiedSession(
        { member: shown.member, organization: shown.organization },
        { member, organization }
      );
      // The page rendered another identity's data: reload into the verified one.
      // Only when the cache now matches it, or the next load would render the
      // stale identity and reload again. The state update below still runs, so
      // a reload cancelled at a leave-page prompt shows the verified identity.
      if (
        reconciliation === 'reload' &&
        writeSessionCache(auth_token, member, organization)
      ) {
        window.location.reload();
      }
    }

    applyMemberPermissions(member);

    // The server resolves user choice, then org default, then English.
    await applySessionLanguage(sessionEffectiveLanguage(member));
    // A logout or another login during the awaits above owns the session now.
    if (verifiedTokenChanged(auth_token)) return;

    set({
      isLoading: false,
      isProvisional: false,
      isAuthenticated: true,
      supportAccess,
      authToken: auth_token,
      member,
      organization,
    });
    writeSessionCache(auth_token, member, organization);

    // Warm the org variable catalog so the first editor or template modal opens with
    // autocomplete ready. Idle-scheduled, and a no-op once the catalog is loaded.
    if (accountReady) prefetchVariableCatalog();

    if (accountReady && !supportAccess && !emailDiverged) {
      // Same identity or first login: refresh profile extras in the
      // background so session verification never blocks on get_user_info.
      void setLocalStorageFromEmail(member.email_address);
    }
  };

  registerSessionIdentityReader(() => {
    const { isAuthenticated, member, organization } = get();
    return identityFromSession(isAuthenticated, member, organization);
  });

  return {
    ...UNAUTHENTICATED_SESSION_STATE,
    isLoading: true,

    invalidateLocalSession: () => {
      // Require a successful scoped reset before another identity can be shown.
      scopedResetFailed = true;
      try {
        // The originating tab owns shared storage, including its login redirect.
        resetScopedStores({ clearStorageOnFailure: false });
      } finally {
        set(UNAUTHENTICATED_SESSION_STATE);
      }
    },

    logout: async () => {
      try {
        // Try to call server logout endpoint
        await getAuth().authLogout();
      } catch (error) {
        // swallow-ok: A failed server logout must still clear this browser's session in finally.
        console.error('Server logout failed:', error);
      } finally {
        storage.clear();
        get().invalidateLocalSession();
      }
    },

    setOrganizationUiLanguage: (uiLanguage) => {
      const { member, organization } = get();
      if (!organization) return;
      set({
        organization: { ...organization, ui_language: uiLanguage },
        // A member without their own choice follows the org default.
        member:
          member && member.ui_language == null
            ? {
                ...member,
                effective_ui_language: resolveRecruiterLanguage(
                  null,
                  uiLanguage
                ),
              }
            : member,
      });
    },

    setAuthenticatedSession: async (authData) => {
      await applyAuthData(authData);
    },

    // unguarded-effect-fetch-ok: Captured tokens are checked on success and failure before state changes.
    verifySession: async () => {
      // A dropped result still settles verification when storage has lost its
      // token. A replacement token is settled by whoever wrote it.
      const settleDroppedVerification = () => {
        if (storage.get(StorageKey.AuthToken)) return;
        usePermissionsStore.getState().clearPermissions();
        set(UNAUTHENTICATED_SESSION_STATE);
      };

      const authToken = storage.get(StorageKey.AuthToken);

      // If no token, user is not authenticated
      if (!authToken || authToken.length === 0) {
        usePermissionsStore.getState().clearPermissions();
        resetScopedStores();
        set(UNAUTHENTICATED_SESSION_STATE);
        return;
      }

      try {
        // Verify token with server
        const authData = await (takePrefetchedVerification(authToken) ??
          getAuth().authVerifyToken());
        if (verifiedTokenChanged(authToken)) {
          settleDroppedVerification();
          return;
        }
        await applyAuthData(authData);
      } catch (error) {
        if (verifiedTokenChanged(authToken)) {
          settleDroppedVerification();
          return;
        }
        console.error('Session verification failed:', error);
        const status = getApiErrorStatus(error);
        if (status !== 401 && status !== 403) {
          // The server never rejected the session (network error, dev backend
          // still booting, 5xx). Keep the stored token so a later page load
          // can re-verify it; just render this load as unauthenticated.
          if (get().isProvisional) {
            // The page already renders the cached session, possibly with the
            // user's input in it. Keep it rather than unmount the page: every API
            // call still checks the token, and a rejected one goes to login.
            set({ isLoading: false, isProvisional: false });
            return;
          }
          set(UNAUTHENTICATED_SESSION_STATE);
          return;
        }
        // Clear invalid session data
        await get().logout();
      }
    },
  };
};

// Marked pure so a build that imports this module without using the store
// drops it along with its API clients: the interview app reaches it only
// through the entry's recruiter session boot, which it never calls. The
// identity reader registered by createSessionStore goes too, so
// getSessionIdentity returns the empty identity, as an unverified store does.
export const useSessionStore = /* @__PURE__ */ create(createSessionStore);

export interface SessionAuthSnapshot {
  isLoading: boolean;
  isAuthenticated: boolean;
}

// Reads the auth state without subscribing, for callers outside React.
export const getSessionAuthSnapshot = (): SessionAuthSnapshot =>
  useSessionStore.getState();

/**
 * Renders the last verified session while verification is in flight: when the
 * cache belongs to the stored token and identity, the session store and the
 * permissions store are filled from it before the first render, since
 * components read identity and permissions without subscribing. isLoading
 * stays true, so only ProtectedRoute (which checks isProvisional) changes; the
 * verified response then reconciles it (see applyAuthData). Returns whether it
 * applied.
 */
export const hydrateSessionFromCache = (): boolean => {
  const state = useSessionStore.getState();
  if (!state.isLoading || state.isAuthenticated) return false;
  const authToken = storage.get(StorageKey.AuthToken);
  const cache = usableCachedSession({
    cache: parseCachedSession(storage.get(StorageKey.CachedSession)),
    authToken,
    storedEmail: storage.get(StorageKey.Email),
    isSupportUser: useSupportUserStore.getState().isSupportUser,
  });
  if (!cache || !authToken) return false;
  const { member, organization } = cache;
  applyMemberPermissions(member);
  useSessionStore.setState({
    isLoading: true,
    isProvisional: true,
    isAuthenticated: true,
    authToken,
    member,
    organization,
  });
  return true;
};

/** Test-only: restore the store to its pre-verify default. */
export const __resetSessionStoreForTests = (): void => {
  prefetchedVerification = null;
  sessionCacheEnabled = false;
  useSessionStore.setState({
    ...UNAUTHENTICATED_SESSION_STATE,
    isLoading: true,
  });
};

// Starts session verification outside React. App.tsx calls this once at module scope;
// it consumes the request prefetchSessionVerification started from the entry, if any,
// and the main app has no mount-time verify of its own, so this is the one request
// per load.
export const startSessionVerification = (): Promise<void> =>
  useSessionStore.getState().verifySession();
